import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../config/axios.config";
import { useParams } from "react-router-dom";
import { formatPrice, handleError } from "../../utils/functions";
import Preloader from "../../helper/Preloader";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import useCurrency from "../../hooks/useCurrency";

const Invoice = () => {
  const [currency] = useCurrency();
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        const { data } = await axiosInstance.get(`/user/order/${id}`);
        if (!data.error) {
          setData(data.data);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };
    getData();
  }, [id]);

  // Sample static data
  const invoiceData = {
    invoiceNumber: "INV-2024-001",
    date: "2024-03-15",
    dueDate: "2024-03-30",
    status: "PAID",

    company: {
      name: "Your Company Name",
      address: "123 Business Street",
      city: "Business City",
      country: "Country",
      phone: "+1 234 567 890",
      email: "company@example.com",
      website: "www.yourcompany.com",
    },

    client: {
      name: "Client Company Name",
      address: "456 Client Avenue",
      city: "Client City",
      country: "Client Country",
      phone: "+1 987 654 321",
      email: "client@example.com",
    },

    items: [
      {
        id: 1,
        description: "Website Development",
        quantity: 1,
        price: 2500.0,
        total: 2500.0,
      },
      {
        id: 2,
        description: "UI/UX Design",
        quantity: 1,
        price: 1500.0,
        total: 1500.0,
      },
      {
        id: 3,
        description: "Content Writing",
        quantity: 2,
        price: 400.0,
        total: 800.0,
      },
    ],

    subtotal: 4800.0,
    tax: 480.0,
    total: 5280.0,
  };

  if (isLoading) return <Preloader isLoading={isLoading} />;

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-lg-8 col-md-9">
          <div className="card px-20 py-30">
            <div className="card-body p-4">
              {/* Invoice Header */}
              <div className="row mb-4">
                <div className="col-md-12 text-center mb-40">
                  <img
                    src="/assets/images/logo/colour.png"
                    alt="Company Logo"
                    style={{ height: "60px", objectFit: "contain" }}
                  />
                </div>
                <div className="col-md-12 text-center">
                  <h3 className="mt-0">{t("invoice")}</h3>
                </div>
              </div>

              <hr />

              {/* Company and Client Info */}
              <div className="row mb-4 py-20">
                <div className="col-md-4">
                  <h6>{t("argetamInfo")}</h6>
                  <p className="mb-1 text-12">{t("argetamAddress")}</p>
                  <p className="mb-1 text-12">{t("argetamPhone")}</p>
                  <p className="mb-1 text-12">{t("argetamEmail")}</p>
                </div>
                <div className="col-md-4 text-center">
                  <h6>{t("clientInfo")}</h6>
                  <p className="mb-1 text-12">{user?.name}</p>
                  <p className="mb-1 text-12">{user?.address}</p>
                  <p className="mb-1 text-12">{user?.phoneNumber}</p>
                  <p className="mb-1 text-12">{user?.email}</p>
                </div>
                <div className="col-md-4 text-end">
                  <h6>{t("orderInfo")}</h6>
                  <p className="text-12 mb-1">
                    <strong>{t("orderId")}: </strong> {data?._id}
                  </p>
                  <p className="text-12 mb-1">
                    <strong>{t("orderDate")}:</strong> {dayjs(data?.createdAt).format("DD/MM/YYYY")}
                  </p>
                  {data.paymentMethod !== "COD" && (
                    <span className={`badge bg-${invoiceData.status === "PAID" ? "success" : "warning"}`}>
                      {data?.paymentStatus}
                    </span>
                  )}
                </div>
              </div>

              <hr />

              {/* Invoice Items */}
              <div className="table-responsive mb-4">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th style={{ background: "#ff5003" }} className="text-white py-10 px-4">
                        {t("description")}
                      </th>
                      <th className="text-center text-white py-10 px-4" style={{ background: "#ff5003" }}>
                        {t("quantity")}
                      </th>
                      <th className="text-end text-white py-10 px-4" style={{ background: "#ff5003" }}>
                        {t("unitPrice")}
                      </th>
                      <th className="text-end text-white py-10 px-4" style={{ background: "#ff5003" }}>
                        {t("total")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items?.map((item) => (
                      <tr key={item.id}>
                        <td className="py-10 px-4">{item?.product?.name}</td>
                        <td className="text-center py-10 px-4">{item.quantity}</td>
                        <td className="py-10 text-end px-4">{formatPrice(item.price, currency)}</td>
                        <td className="py-10 text-end px-4">
                          {formatPrice(item.price * item.quantity, currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Invoice Summary */}
              <div className="row justify-content-end">
                <div className="col-md-6">
                  <div className="bg-light p-3 rounded">
                    <div className="d-flex justify-content-between mb-2">
                      <span>{t("subtotal")}</span>
                      <strong>{formatPrice(data?.totalAmount - data?.shipmentCharges, currency)}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>{t("shipmentCharges")} </span>
                      <strong>{formatPrice(data?.shipmentCharges, currency)}</strong>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between">
                      <h5>{t("total")}</h5>
                      <h5>{formatPrice(data?.totalAmount, currency)}</h5>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row my-40">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h5>{t("paymentDetails")}</h5>
                    <p className="mb-1">{data?.paymentMethod === "COD" ? "Cash On Delivery" : "Online"} </p>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <h5>{t("notes")}</h5>
                  <p className="text-12">{t("invoiceNotes")}</p>
                </div>
              </div>

              {/* Invoice Footer */}
              {/* <div className="text-center mt-4">
                <button className="btn btn-success me-2">
                  <i className="bi bi-download me-2"></i> {t("print")}
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
